import React from 'react';
import SEO from '../components/SEO';
import './RewardlyAI.css';

const AndroidIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.523 15.341a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zm-9.219 0a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zM17.9 10l1.448-2.508a.302.302 0 00-.11-.412.302.302 0 00-.412.11l-1.468 2.542A8.965 8.965 0 0012 8.545c-1.97 0-3.79.635-5.258 1.687L5.274 7.19a.302.302 0 00-.412-.11.302.302 0 00-.11.412L6.2 10c-2.133 1.454-3.548 3.795-3.548 6.453 0 .303.017.602.048.898h18.6c.031-.296.048-.595.048-.898C21.348 13.795 19.933 11.454 17.9 10z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const features = [
  { icon: '💳', title: 'Smart Expense Tracking', desc: 'Categorize your expenses effortlessly and see exactly where your money goes. Monthly and one-time totals with improved calculation accuracy.' },
  { icon: '✈️', title: 'Plan Trips with Reward Points', desc: 'Use Rewardly AI to plan and book trips using your accumulated reward points. Compare domestic and international destinations and maximize value.' },
  { icon: '🗂️', title: 'Credit Card Management', desc: 'Add and manage all your credit cards in one place. Track reward points, view card details, and get personalized recommendations for new cards.' },
  { icon: '👥', title: 'Community-Driven Optimization', desc: 'Join a community of smart spenders. Share reward strategies, ask questions, and get expert suggestions on maximizing credit card benefits.' },
  { icon: '🔍', title: 'Smart Reward Search', desc: 'Search for the best reward options by expense category. Our intelligent system finds perfect credit card rewards for groceries, dining, travel, and more.' },
  { icon: '🤖', title: 'AI Card Recommendations', desc: 'Not sure which credit card is right for you? Get AI-powered recommendations based on your spending patterns, lifestyle, and financial goals.' },
];

const realScreenshots = [
  { src: '/images/rewardly-ai/rewardly-ai-screenshot-expense-tracking.jpg', alt: 'Rewardly AI – smart expense tracking screen' },
  { src: '/images/rewardly-ai/rewardly-ai-screenshot-trip-planning.jpg', alt: 'Rewardly AI – plan trips with reward points screen' },
  { src: '/images/rewardly-ai/rewardly-ai-screenshot-card-management.jpg', alt: 'Rewardly AI – credit card management screen' },
  { src: '/images/rewardly-ai/rewardly-ai-screenshot-reward-search.jpg', alt: 'Rewardly AI – smart reward search screen' },
  { src: '/images/rewardly-ai/rewardly-ai-screenshot-ai-recommendations.jpg', alt: 'Rewardly AI – AI card recommendations screen' },
];

const RewardlyAI = () => (
  <div className="product-page">
    <SEO
      title="Rewardly AI: Card Rewards — Maximize Every Purchase | Appshow"
      description="Your complete credit card rewards companion. Maximize every purchase, plan trips with points, get AI-powered card recommendations, and join a community of reward optimizers."
      keywords="credit card rewards, reward maximizer, expense tracker, points optimizer, card recommendations, travel rewards, cashback app"
      url="/rewardly-ai-card-rewards"
    />

    <section className="pp-hero">
      <div className="container pp-hero-inner">
        <div className="pp-hero-content">
        <div className="pp-hero-meta">
          <span className="badge-live">Live</span>
          <span className="pp-category">Finance</span>
        </div>
        <h1 className="pp-title">Rewardly AI</h1>
        <p className="pp-subtitle">Maximize Your Card Rewards</p>
        <p className="pp-description">
          Your complete credit card rewards companion. Maximize every purchase and turn your spending into
          incredible rewards. Whether you're planning your dream vacation, tracking daily expenses, or
          optimizing your credit card strategy, Rewardly AI makes it simple and rewarding.
        </p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.rewards" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon />
            <span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/in/app/rewardly-ai-card-rewards/id6756515642" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon />
            <span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
          <a href="https://rewardlyai.com/" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <GlobeIcon />
            <span><span className="btn-store-sub">Visit</span><span className="btn-store-main">rewardlyai.com</span></span>
          </a>
        </div>
        <p className="pp-note">Free · In-App Purchases · iOS 15.1+ · Android · 3.7★ Rating</p>
        </div>
        <div className="pp-hero-preview">
          <img
            src="/images/rewardly-ai/rewardly-ai-card-rewards-app-preview.png"
            alt="Rewardly AI card rewards app preview – maximize credit card rewards"
            className="pp-preview-img"
          />
        </div>
      </div>
    </section>

    <section className="pp-screenshots">
      <div className="container">
        <h2 className="pp-section-title">Screenshots</h2>
        <div className="screenshots-row">
          {realScreenshots.map((s, i) => (
            <div className="phone-frame" key={i}>
              <img src={s.src} alt={s.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-features">
      <div className="container">
        <h2 className="pp-section-title">Key Features</h2>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-cta">
      <div className="container">
        <h2>Start maximizing your rewards today</h2>
        <p>Join thousands of smart spenders earning more from every purchase.</p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.rewards" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon /><span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/in/app/rewardly-ai-card-rewards/id6756515642" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon /><span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
          <a href="https://rewardlyai.com/" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <GlobeIcon /><span><span className="btn-store-sub">Visit</span><span className="btn-store-main">rewardlyai.com</span></span>
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default RewardlyAI;
